import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'
import { config } from './config'

/**
 * Browser OIDC for the admin panel: Authorization Code + PKCE against Keycloak.
 *
 * Two deliberate choices:
 *
 * - `sessionStorage`, not `localStorage`. Tokens are cleared when the tab
 *   closes, so an admin session does not survive on a shared or unattended
 *   machine. It also scopes the session to one tab.
 * - No client secret. A secret in a browser bundle is public, which is exactly
 *   the case PKCE exists for; Keycloak's client must be configured as public.
 *
 * Holding a token here grants nothing on its own — every `/api/admin/**` route
 * checks the realm role server-side.
 */
export const userManager = new UserManager({
  authority: config.issuerUrl,
  client_id: config.clientId,
  redirect_uri: config.redirectUri,
  post_logout_redirect_uri: config.postLogoutRedirectUri,
  response_type: 'code',
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Refresh shortly before expiry so a long-running screen does not fail
  // mid-action with a 401.
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
})

export async function signIn(): Promise<void> {
  await userManager.signinRedirect()
}

export async function completeSignIn(): Promise<User> {
  return userManager.signinRedirectCallback()
}

export async function signOut(): Promise<void> {
  // Clear locally first so a failure to reach Keycloak still ends the session
  // in this browser.
  await userManager.removeUser()
  try {
    await userManager.signoutRedirect()
  } catch {
    window.location.href = config.postLogoutRedirectUri
  }
}

export async function currentUser(): Promise<User | null> {
  const user = await userManager.getUser()
  return user && !user.expired ? user : null
}

export async function accessToken(): Promise<string | null> {
  const user = await currentUser()
  return user?.access_token ?? null
}

/** Realm roles from the access token, used only to decide what to render. */
export function realmRoles(user: User | null): string[] {
  if (!user?.access_token) return []
  try {
    const payload = user.access_token.split('.')[1]
    if (!payload) return []
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      realm_access?: { roles?: string[] }
    }
    return decoded.realm_access?.roles ?? []
  } catch {
    return []
  }
}

/**
 * Whether to show the admin UI at all.
 *
 * A convenience only: hiding navigation for a non-admin is a nicer experience
 * than a wall of 403s, but it is not the control. The API refuses the request
 * regardless of what this returns.
 */
export function isAdmin(user: User | null): boolean {
  return realmRoles(user).includes(import.meta.env.VITE_ADMIN_ROLE ?? 'pixviro-admin')
}
